/**
 * @prettier
 */

import React, { useReducer } from 'react'
import ItemContext from './ItemContext'
import itemReducer from './itemReducer'
import {
  ADD_ITEM,
  DELETE_ITEM,
  UPDATE_ITEM,
  SET_CURRENT,
  CLEAR_CURRENT,
  GET_ITEMS,
  ITEM_ERROR,
} from '../types'
import axios from 'axios'

const ItemState = (props) => {
  const initialState = {
    items: [],
    currentItem: null,
  }

  const [state, dispatch] = useReducer(itemReducer, initialState)

  const getItem = async (id) => {
    try {
      const res = await axios.get(`/api/items/${id}`)
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const getItems = async () => {
    try {
      const res = await axios.get('/api/items')
      dispatch({ type: GET_ITEMS, payload: res.data })
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const addItem = async (item) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    }
    try {
      const res = await axios.post('/api/items', item, config)
      dispatch({ type: ADD_ITEM, payload: res.data })
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const updateItem = async (item) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    }
    try {
      const res = await axios.put(`/api/items/${item._id}`, item, config)
      dispatch({ type: UPDATE_ITEM, payload: res.data })
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const deleteItem = async (id) => {
    try {
      await axios.delete(`/api/items/${id}`)
      dispatch({ type: DELETE_ITEM, payload: id })
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const setCurrentItem = (item) => {
    dispatch({ type: SET_CURRENT, payload: item })
  }

  const clearCurrentItem = () => {
    dispatch({ type: CLEAR_CURRENT })
  }

  // ItemHome relationship methods
  const getItemHomes = async (itemId) => {
    try {
      const res = await axios.get(`/api/itemhomes/item/${itemId}`)
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const addItemHome = async (itemId, homeId) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    }
    try {
      const res = await axios.post('/api/itemhomes', { itemId, homeId }, config)
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
      throw err
    }
  }

  const removeItemHome = async (itemId, homeId) => {
    try {
      await axios.delete(`/api/itemhomes/item/${itemId}/home/${homeId}`)
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  // ItemStore relationship methods
  const getItemStores = async (itemId) => {
    try {
      const res = await axios.get(`/api/itemstores/item/${itemId}`)
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  const addItemStore = async (itemId, storeId) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    }
    try {
      const res = await axios.post(
        '/api/itemstores',
        { itemId, storeId },
        config
      )
      return res.data
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
      throw err
    }
  }

  const removeItemStore = async (itemId, storeId) => {
    try {
      await axios.delete(`/api/itemstores/item/${itemId}/store/${storeId}`)
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
    }
  }

  // Get items filtered by home and store (for list creation)
  const getItemsByHomeAndStore = async (homeId, storeId) => {
    try {
      // Get all items in the home
      const itemHomesRes = await axios.get(`/api/itemhomes/home/${homeId}`)
      const itemHomes = itemHomesRes.data

      // Get all items at the store
      const itemStoresRes = await axios.get(`/api/itemstores/store/${storeId}`)
      const itemStores = itemStoresRes.data

      // Find intersection: items that are both in the home AND at the store
      const itemsInHomeIds = new Set(itemHomes.map((ih) => ih.item._id))
      const itemsAtStore = itemStores.filter((is) =>
        itemsInHomeIds.has(is.item._id)
      )

      // Return the items (with full item data from itemStores)
      return itemsAtStore.map((is) => is.item)
    } catch (err) {
      dispatch({ type: ITEM_ERROR, payload: err })
      return []
    }
  }

  return (
    <ItemContext.Provider
      value={{
        items: state.items,
        currentItem: state.currentItem,
        getItem,
        getItems,
        addItem,
        updateItem,
        deleteItem,
        setCurrentItem,
        clearCurrentItem,
        getItemHomes,
        addItemHome,
        removeItemHome,
        getItemStores,
        addItemStore,
        removeItemStore,
        getItemsByHomeAndStore,
      }}
    >
      {props.children}
    </ItemContext.Provider>
  )
}

export default ItemState
